import { createReducer, on } from '@ngrx/store';
import { initialState } from './state';
import * as LocationCardActions from '../actions';
import { FlexBasicInfoFieldId, FlexInfoFieldId, FlexSectionIds } from '../configs/flex/base-config-models';
import { FieldType } from '../models/field-models';
import {
    FlexEntityTypeFieldState,
    flexCheckMp2,
    flexMonitorPointChange,
    getFlexEntityTypeFieldState,
    sortFlexSections,
} from '../configs/flex/utils';
import { getDynamicSectionByName } from '../configs/dynamic-section-utils';
import { parseDynamicSection } from '../core/dynamic-section-parser';
import { normalizeRow, normalizeSection } from '../config.models';
import { BasicInfoCommonFieldIds, CommonSectionIds, SensorTypes } from '../configs/common.models';
import { FlexLocationCardState, FlexScanModeOption, FlexScanModeValue } from '../configs/flex/models';
import { FlowType, SampleRateElement } from '../../models/location-card.model';
import {
    DynamicSection,
    RegisterEntityType,
    SensorConfiguration,
    SensorMonitoringPointDataSource,
} from '../models/dynamic-section-models';
import { findFieldById, findSectionById, findSelectFieldById } from '../configs/base-utils';
import { CreateFlexTextRow, getFlexCreateModeElement } from '../configs/flex/basic-info';
import { shouldLockFlexScanRate } from '../configs/flex/scan-mode';
import { MonitorSeriesNames } from '../../location-card.constants';

const NONE_SENSOR = 'None';

function updateFlexScanModeLock(state: FlexLocationCardState): FlexLocationCardState {
    if (!state.formState) {
        return state;
    }

    const formState = { ...state.formState };
    const scanModeField = findSelectFieldById<FlexScanModeOption, FlexScanModeValue>(
        formState,
        FlexBasicInfoFieldId.ScanMode,
    );

    if (!scanModeField) {
        return state;
    }

    const normalStorageInterval = findSelectFieldById<SampleRateElement, number>(
        formState,
        BasicInfoCommonFieldIds.SampleRate,
    )?.value;
    const fastStorageInterval = findSelectFieldById<SampleRateElement, number>(
        formState,
        BasicInfoCommonFieldIds.FastRate,
    )?.value;
    const isLocked = shouldLockFlexScanRate(normalStorageInterval, fastStorageInterval);

    scanModeField.disabled = isLocked;
    if (isLocked) {
        scanModeField.value = FlexScanModeValue.Normal;
    }

    return { ...state, formState };
}

function isMonitoringPoint(value: unknown): value is 0 | 1 {
    return value === 0 || value === 1;
}

function applyEntityTypeFieldState(
    formState: FlexLocationCardState['formState'],
    fieldId: FlexInfoFieldId,
    fieldState: FlexEntityTypeFieldState,
): void {
    const field = findFieldById(formState, fieldId, FieldType.Select);
    if (!field) {
        return;
    }

    field.value = fieldState.value;
    field.visible = fieldState.visible;
    field.readonly = fieldState.readonly;
    field.disabled = fieldState.disabled;
}

function configureSensorEntityTypes(
    formState: FlexLocationCardState['formState'],
    sensorIndex: 0 | 1,
    template: DynamicSection | undefined,
    currentPreferences: SensorMonitoringPointDataSource | null | undefined,
    preserveCurrentValue: boolean,
): SensorMonitoringPointDataSource {
    const depthFieldState = getFlexEntityTypeFieldState(
        template?.sourcesforDepth,
        currentPreferences?.depthEntityType,
        preserveCurrentValue,
    );
    const velocityFieldState = getFlexEntityTypeFieldState(
        template?.sourcesforVelocity,
        currentPreferences?.velocityEntityType,
        preserveCurrentValue,
    );
    const depthFieldId = sensorIndex === 0 ? FlexInfoFieldId.Mp1DepthEntityType : FlexInfoFieldId.Mp2DepthEntityType;
    const velocityFieldId =
        sensorIndex === 0 ? FlexInfoFieldId.Mp1VelocityEntityType : FlexInfoFieldId.Mp2VelocityEntityType;
    const titleFieldId =
        sensorIndex === 0 ? FlexInfoFieldId.Mp1SensorPreferencesTitle : FlexInfoFieldId.Mp2SensorPreferencesTitle;
    const flowTypeFieldId = sensorIndex === 0 ? FlexInfoFieldId.FlowType1 : FlexInfoFieldId.FlowType2;

    applyEntityTypeFieldState(formState, depthFieldId, depthFieldState);
    applyEntityTypeFieldState(formState, velocityFieldId, velocityFieldState);

    const flowTypeField = findFieldById(formState, flowTypeFieldId, FieldType.Select);
    if (flowTypeField) {
        flowTypeField.visible = velocityFieldState.visible;
    }

    const titleField = findFieldById(formState, titleFieldId, FieldType.UI);
    if (titleField) {
        titleField.visible = depthFieldState.visible || velocityFieldState.visible;
    }

    const updatedPreferences: SensorMonitoringPointDataSource = {
        ...currentPreferences,
        depthEntityType: depthFieldState.preference,
    };

    if (velocityFieldState.preference === undefined) {
        delete updatedPreferences.velocitySensorId;
        delete updatedPreferences.velocityEntityType;
    } else {
        updatedPreferences.velocityEntityType = velocityFieldState.preference;
    }

    return updatedPreferences;
}

export const flexReducer = createReducer(
    initialState as FlexLocationCardState,
    on(LocationCardActions.evaluateFlexScanModeLock, (state) => updateFlexScanModeLock(state)),
    on(LocationCardActions.setGlobalConfig, (state, { monitorSeriesName }) =>
        monitorSeriesName === MonitorSeriesNames.flex ? updateFlexScanModeLock(state) : state,
    ),
    on(LocationCardActions.flexSensorChange, (state, { sensor, index }) => {
        const formState = { ...state.formState };

        const flexSection = formState.config.sections.find((section) => section.id === FlexSectionIds.FlexInfo);

        if (!flexSection) {
            console.error(`FLEX section not found`);
            return state;
        }

        const mp1Field = findFieldById(formState, FlexInfoFieldId.MP1, FieldType.Select);
        const mp2Field = findFieldById(formState, FlexInfoFieldId.MP2, FieldType.Select);
        const channel1Field = findFieldById(formState, FlexInfoFieldId.Channel1, FieldType.Select);
        const channel2Field = findFieldById(formState, FlexInfoFieldId.Channel2, FieldType.Select);
        if (!mp1Field || !mp2Field || !channel1Field || !channel2Field) {
            return state;
        }

        const selectedMp = index === 0 ? mp1Field.value : mp2Field.value;
        const mp = isMonitoringPoint(selectedMp) ? selectedMp : index;
        const oldSensorSection = formState.config.sections.find(
            (v) => v.metadata?.type === SensorTypes.flex && v.metadata?.sensorIndex === index,
        );
        const existingSensorId = oldSensorSection?.metadata?.id ?? null;
        const existingSensorMp = isMonitoringPoint(oldSensorSection?.metadata?.mp) ? oldSensorSection.metadata.mp : mp;
        const newSensor =
            sensor.name === NONE_SENSOR ? undefined : getDynamicSectionByName(state, sensor.name, mp, index);
        if (sensor.name !== NONE_SENSOR && !newSensor) {
            return state;
        }

        const mp1FlowType = findFieldById(formState, FlexInfoFieldId.FlowType1, FieldType.Select);
        const mp2FlowType = findFieldById(formState, FlexInfoFieldId.FlowType2, FieldType.Select);
        if (mp1FlowType) {
            mp1FlowType.value = FlowType.Normal;
        }
        if (mp2FlowType) {
            mp2FlowType.value = FlowType.Normal;
        }

        const monitorConfiguration = state.monitorConfiguration;
        let mp1Preferences = monitorConfiguration?.mp1Preferences;
        let mp2Preferences = monitorConfiguration?.mp2Preferences;

        if (sensor.name === NONE_SENSOR) {
            configureSensorEntityTypes(formState, index, undefined, undefined, false);
            if (existingSensorMp === 0) {
                mp1Preferences = null;
            } else {
                mp2Preferences = null;
            }
        } else {
            const currentPreferences = mp === 0 ? mp1Preferences : mp2Preferences;
            const updatedPreferences = configureSensorEntityTypes(
                formState,
                index,
                newSensor,
                currentPreferences,
                false,
            );

            if (mp === 0) {
                mp1Preferences = updatedPreferences;
            } else {
                mp2Preferences = updatedPreferences;
            }
        }

        const updatedMonitorConfiguration = monitorConfiguration
            ? {
                  ...monitorConfiguration,
                  mp1Preferences,
                  mp2Preferences,
              }
            : monitorConfiguration;

        formState.config.sections = formState.config.sections.filter(
            (v) => !v.metadata || v.metadata.type !== SensorTypes.flex || v.metadata.sensorIndex !== index,
        );

        // None sensor selected
        if (sensor.name === NONE_SENSOR) {
            mp2Field.value = null;
            mp2Field.disabled = true;

            channel2Field.value = null;
            channel2Field.disabled = true;

            if (mp1Field.value !== 0) {
                mp1Field.value = 0;
                flexMonitorPointChange(formState, 0);
            }

            flexCheckMp2(state);
            sortFlexSections(formState);

            const newState = { ...state, formState, monitorConfiguration: updatedMonitorConfiguration };
            return { ...newState, options: { ...state.options } };
        }

        const newSensorConfig = parseDynamicSection(newSensor, mp, undefined, state.settings);
        const normalizedSensor = normalizeSection(newSensorConfig);
        normalizedSensor.metadata = {
            ...normalizedSensor.metadata,
            sensorIndex: index,
            mp,
            type: SensorTypes.flex,
            id: existingSensorId,
        };
        formState.config.sections.push(normalizedSensor);

        // // first change handler
        const section1FirstChange = mp1Field.value === null && mp2Field.value === null;
        if (section1FirstChange) {
            mp1Field.value = 0;
            channel1Field.value = 1;
        }

        const section2FirstChange = index === 1 && mp2Field.value === null;
        if (section2FirstChange) {
            mp2Field.disabled = false;
            channel2Field.disabled = false;
            mp2Field.value = 1;
            channel2Field.value = channel1Field.value === 1 ? 2 : 1;
        }

        flexCheckMp2(state);
        sortFlexSections(formState);

        return {
            ...state,
            formState,
            monitorConfiguration: updatedMonitorConfiguration,
            options: { ...state.options },
        };
    }),

    on(LocationCardActions.flexChannelChange, (state, { index }) => {
        const formState = { ...state.formState };
        const flexInfo = formState.config.sections.find((section) => section.id === FlexSectionIds.FlexInfo);
        const channel1 = flexInfo.fields[FlexInfoFieldId.Channel1];
        const channel2 = flexInfo.fields[FlexInfoFieldId.Channel2];

        const updatedChannel = index === 0 ? channel1 : channel2;
        const toUpdateChannel = index === 0 ? channel2 : channel1;

        if (updatedChannel.value === toUpdateChannel.value) {
            toUpdateChannel.value = updatedChannel.value === 1 ? 2 : 1;
        }

        return { ...state, formState, options: { ...state.options } };
    }),

    on(LocationCardActions.flexMonitorPointChange, (state, { index }) => {
        const formState = { ...state.formState };
        flexMonitorPointChange(formState, index);

        flexCheckMp2(state);
        sortFlexSections(formState);

        return { ...state, formState, options: { ...state.options } };
    }),

    on(LocationCardActions.loadExistingDynamicSectionsSuccess, (state, { data }) => {
        const formState = { ...state.formState };

        if (!data?.length || !state.options.loadedDynamicConfigs?.length) {
            console.warn('No existing dynamic sections or no templates found');
            return state;
        }

        const prepopulatedSections = data.reduce<
            Array<{ template: DynamicSection; existingConfig: SensorConfiguration; sensorIndex: 0 | 1 }>
        >((sections, existingConfig, sensorIndex) => {
            if (sensorIndex !== 0 && sensorIndex !== 1) {
                return sections;
            }

            const matchingTemplate = state.options.loadedDynamicConfigs.find(
                (template) => template.id.name === existingConfig.sensorName,
            );

            if (!matchingTemplate) {
                console.warn(`No matching template found for ${existingConfig.sensorName}`);
                return sections;
            }

            sections.push({ template: matchingTemplate, existingConfig, sensorIndex });
            return sections;
        }, []);

        const sensor1Field = findFieldById(formState, FlexInfoFieldId.Sensor1, FieldType.Select);
        const sensor2Field = findFieldById(formState, FlexInfoFieldId.Sensor2, FieldType.Select);
        const mp1Field = findFieldById(formState, FlexInfoFieldId.MP1, FieldType.Select);
        const mp2Field = findFieldById(formState, FlexInfoFieldId.MP2, FieldType.Select);
        const channel1Field = findFieldById(formState, FlexInfoFieldId.Channel1, FieldType.Select);
        const channel2Field = findFieldById(formState, FlexInfoFieldId.Channel2, FieldType.Select);
        if (!sensor1Field || !sensor2Field || !mp1Field || !mp2Field || !channel1Field || !channel2Field) {
            return state;
        }

        const [firstSensor, secondSensor] = data;
        let mp1Preferences = state.monitorConfiguration?.mp1Preferences;
        let mp2Preferences = state.monitorConfiguration?.mp2Preferences;

        if (firstSensor && isMonitoringPoint(firstSensor.monitoringPoint)) {
            sensor1Field.value = firstSensor.sensorName;
            mp1Field.value = firstSensor.monitoringPoint;
            channel1Field.value = firstSensor.channel;

            const template = prepopulatedSections.find((section) => section.sensorIndex === 0)?.template;
            const currentPreferences = firstSensor.monitoringPoint === 0 ? mp1Preferences : mp2Preferences;
            const updatedPreferences = configureSensorEntityTypes(formState, 0, template, currentPreferences, true);

            if (firstSensor.monitoringPoint === 0) {
                mp1Preferences = updatedPreferences;
            } else {
                mp2Preferences = updatedPreferences;
            }
        }

        if (secondSensor && isMonitoringPoint(secondSensor.monitoringPoint)) {
            sensor2Field.value = secondSensor.sensorName;
            mp2Field.value = secondSensor.monitoringPoint;
            channel2Field.value = secondSensor.channel;

            const template = prepopulatedSections.find((section) => section.sensorIndex === 1)?.template;
            const currentPreferences = secondSensor.monitoringPoint === 0 ? mp1Preferences : mp2Preferences;
            const updatedPreferences = configureSensorEntityTypes(formState, 1, template, currentPreferences, true);

            if (secondSensor.monitoringPoint === 0) {
                mp1Preferences = updatedPreferences;
            } else {
                mp2Preferences = updatedPreferences;
            }

            mp2Field.disabled = false;
            channel2Field.disabled = false;
        }

        prepopulatedSections.forEach(({ template, existingConfig, sensorIndex }) => {
            // #53601 temporarily disable second sensor
            if (sensorIndex === 1 || !isMonitoringPoint(existingConfig.monitoringPoint)) {
                return;
            }

            const newSensorConfig = parseDynamicSection(
                template,
                existingConfig.monitoringPoint,
                existingConfig,
                state.settings,
            );
            const normalizedSensor = normalizeSection(newSensorConfig);
            normalizedSensor.metadata = {
                ...normalizedSensor.metadata,
                sensorIndex,
                mp: existingConfig.monitoringPoint,
                type: SensorTypes.flex,
                id: existingConfig.id,
            };
            formState.config.sections.push(normalizedSensor);
        });

        const monitorConfiguration = state.monitorConfiguration
            ? { ...state.monitorConfiguration, mp1Preferences, mp2Preferences }
            : state.monitorConfiguration;
        const newState = { ...state, formState, monitorConfiguration };

        flexCheckMp2(newState);
        sortFlexSections(formState);

        return { ...newState, options: { ...state.options } };
    }),

    on(LocationCardActions.setFlexSiltValue, (state, { mp, value }) => {
        const formState = { ...state.formState };
        const flexInfo = formState.config.sections.find((section) => section.id === FlexSectionIds.FlexInfo);

        if (!flexInfo) {
            console.error(`FLEX section not found`);
            return state;
        }

        const siltField1 = findFieldById(formState, FlexInfoFieldId.Silt1, FieldType.Number);
        const siltField2 = findFieldById(formState, FlexInfoFieldId.Silt2, FieldType.Number);

        if (mp === 0 && siltField1) {
            siltField1.value = value ?? 0;
        } else if (mp === 1 && siltField2) {
            siltField2.value = value ?? 0;
        }

        return { ...state, formState };
    }),

    on(LocationCardActions.flexSetCreateMode, (state, { mode, message }) => {
        const currentMode = state.metadata.flexCreateMode;

        if (currentMode && currentMode === mode) {
            return state;
        }

        const section = findSectionById(state.formState, CommonSectionIds.BasicInfo);
        if (!section) {
            return { ...state, metadata: { ...state.metadata, flexCreateMode: mode } };
        }

        const modeElement = getFlexCreateModeElement(mode);
        if (message) {
            modeElement.label = message;
        }

        const newRow = CreateFlexTextRow(modeElement);

        // Normalize the new row
        const { normalizedRow, fields } = normalizeRow(newRow, 1, section.id, section.type, CommonSectionIds.BasicInfo);

        const newFormState = { ...state.formState };
        const newSection = { ...section };

        const rowIndex = newSection.rows.findIndex((row) => row.id === FlexInfoFieldId.CreateFlexTextRow);
        if (rowIndex !== -1) {
            newSection.rows[rowIndex] = normalizedRow;

            Object.assign(newSection.fields, fields);

            const sectionIndex = newFormState.config.sections.findIndex((s) => s.id === CommonSectionIds.BasicInfo);
            if (sectionIndex !== -1) {
                newFormState.config.sections[sectionIndex] = newSection;
            }
        }

        return {
            ...state,
            formState: newFormState,
            metadata: { ...state.metadata, flexCreateMode: mode },
        };
    }),

    on(LocationCardActions.loadSensorCollectionSuccess, (state, { collection }) => {
        const formState = { ...state.formState };
        const mp1DepthField = findFieldById(formState, FlexInfoFieldId.Mp1DepthEntityType, FieldType.Select);
        const mp1VelocityField = findFieldById(formState, FlexInfoFieldId.Mp1VelocityEntityType, FieldType.Select);
        const mp2DepthField = findFieldById(formState, FlexInfoFieldId.Mp2DepthEntityType, FieldType.Select);
        const mp2VelocityField = findFieldById(formState, FlexInfoFieldId.Mp2VelocityEntityType, FieldType.Select);

        if (mp1DepthField) {
            mp1DepthField.value = collection.mp_1?.depthEntityType ?? RegisterEntityType.Auto;
        }
        if (mp1VelocityField) {
            mp1VelocityField.value = collection.mp_1?.velocityEntityType ?? RegisterEntityType.Auto;
        }
        if (mp2DepthField) {
            mp2DepthField.value = collection.mp_2?.depthEntityType ?? RegisterEntityType.Auto;
        }
        if (mp2VelocityField) {
            mp2VelocityField.value = collection.mp_2?.velocityEntityType ?? RegisterEntityType.Auto;
        }

        let mp1Preferences = collection.mp_1 ?? null;
        let mp2Preferences = collection.mp_2 ?? null;
        const sensor1Field = findFieldById(formState, FlexInfoFieldId.Sensor1, FieldType.Select);
        const sensor2Field = findFieldById(formState, FlexInfoFieldId.Sensor2, FieldType.Select);
        const sensor1MpField = findFieldById(formState, FlexInfoFieldId.MP1, FieldType.Select);
        const sensor2MpField = findFieldById(formState, FlexInfoFieldId.MP2, FieldType.Select);

        if (typeof sensor1Field?.value === 'string' && isMonitoringPoint(sensor1MpField?.value)) {
            const template = state.options.loadedDynamicConfigs?.find(
                (config) => config.id.name === sensor1Field.value,
            );
            const currentPreferences = sensor1MpField.value === 0 ? mp1Preferences : mp2Preferences;
            const updatedPreferences = configureSensorEntityTypes(formState, 0, template, currentPreferences, true);

            if (sensor1MpField.value === 0) {
                mp1Preferences = updatedPreferences;
            } else {
                mp2Preferences = updatedPreferences;
            }
        }

        if (
            typeof sensor2Field?.value === 'string' &&
            sensor2Field.value !== NONE_SENSOR &&
            isMonitoringPoint(sensor2MpField?.value)
        ) {
            const template = state.options.loadedDynamicConfigs?.find(
                (config) => config.id.name === sensor2Field.value,
            );
            const currentPreferences = sensor2MpField.value === 0 ? mp1Preferences : mp2Preferences;
            const updatedPreferences = configureSensorEntityTypes(formState, 1, template, currentPreferences, true);

            if (sensor2MpField.value === 0) {
                mp1Preferences = updatedPreferences;
            } else {
                mp2Preferences = updatedPreferences;
            }
        }

        return {
            ...state,
            formState,
            monitorConfiguration: state.monitorConfiguration
                ? { ...state.monitorConfiguration, mp1Preferences, mp2Preferences }
                : state.monitorConfiguration,
        };
    }),

    on(LocationCardActions.updateMp1Preferences, (state, { preferences }) => ({
        ...state,
        monitorConfiguration: {
            ...state.monitorConfiguration,
            mp1Preferences: preferences,
        },
    })),

    on(LocationCardActions.updateMp2Preferences, (state, { preferences }) => ({
        ...state,
        monitorConfiguration: {
            ...state.monitorConfiguration,
            mp2Preferences: preferences,
        },
    })),

    on(LocationCardActions.clearMp2Preferences, (state) => ({
        ...state,
        monitorConfiguration: {
            ...state.monitorConfiguration,
            mp2Preferences: null,
        },
    })),

    on(LocationCardActions.updateMp1DepthEntityType, (state, { entityType }) => ({
        ...state,
        monitorConfiguration: {
            ...state.monitorConfiguration,
            mp1Preferences: {
                ...state.monitorConfiguration?.mp1Preferences,
                depthEntityType: entityType,
            },
        },
    })),

    on(LocationCardActions.updateMp1VelocityEntityType, (state, { entityType }) => ({
        ...state,
        monitorConfiguration: {
            ...state.monitorConfiguration,
            mp1Preferences: {
                ...state.monitorConfiguration?.mp1Preferences,
                velocityEntityType: entityType,
            },
        },
    })),

    on(LocationCardActions.updateMp2DepthEntityType, (state, { entityType }) => ({
        ...state,
        monitorConfiguration: {
            ...state.monitorConfiguration,
            mp2Preferences: {
                ...state.monitorConfiguration?.mp2Preferences,
                depthEntityType: entityType,
            },
        },
    })),

    on(LocationCardActions.updateMp2VelocityEntityType, (state, { entityType }) => ({
        ...state,
        monitorConfiguration: {
            ...state.monitorConfiguration,
            mp2Preferences: {
                ...state.monitorConfiguration?.mp2Preferences,
                velocityEntityType: entityType,
            },
        },
    })),
);
